import { webDarkTheme, webLightTheme } from '@fluentui/react-components'
import { useCallback, useEffect, useState } from 'react'
import type { ThemeMode } from '../contexts/ThemeContext'
import { ThemeContext } from '../contexts/ThemeContext'

interface ThemeProviderProps {
  children: React.ReactNode
}

function getSystemPreference(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

function saveThemeToLocalStorage(theme: ThemeMode) {
  try {
    localStorage.setItem('theme-mode', theme)
  } catch (error) {
    console.warn('Could not save the theme to localStorage:', error)
  }
}

function getPreferredTheme() {
  // Check localStorage first
  try {
    const saved = localStorage.getItem('theme-mode')
    if (saved === 'light' || saved === 'dark') {
      return saved
    }
  } catch (error) {
    console.warn('Could not read the theme from localStorage:', error)
  }

  // If no localStorage value, get system preference
  const systemPreference = getSystemPreference()

  // Save the system preference to localStorage
  saveThemeToLocalStorage(systemPreference)

  return systemPreference
}

export const ThemeContextProvider = ({ children }: ThemeProviderProps) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => getPreferredTheme())

  const isDark = themeMode === 'dark'
  const theme = isDark ? webDarkTheme : webLightTheme

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
  }, [isDark])

  const handleSetThemeMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode)
    saveThemeToLocalStorage(mode)
  }, [])

  return (
    <ThemeContext
      value={{
        theme,
        themeMode,
        isDark,
        setThemeMode: handleSetThemeMode,
      }}>
      {children}
    </ThemeContext>
  )
}
